import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import api, { clearPersistedGridFilters } from "../services/api";
import { isCarrierUser } from "../utils/menuItems";

/**
 * Subsistema ao qual o login pertence. As permissões são compartilhadas por
 * chave (ex.: "carrossel") entre o menu do pátio e o da transportadora, então
 * precisamos saber o subsistema para não deixar uma permissão do PÁTIO liberar
 * a mesma tela no módulo TRANSPORTADORA (e vice-versa).
 *
 * O subsistema do grupo escolhido no login (PATIO, TERMINAL, ...) tem
 * prioridade: as permissões de um grupo TERMINAL são gravadas com subsistema
 * TERMINAL, e tratá-lo como PATIO descartava todas elas.
 */
export const getSubsistema = (
  user?: { grupo?: string; subsistema?: string | null; id_transportadora?: number | null } | null
): string =>
  user?.subsistema?.toUpperCase() || (isCarrierUser(user) ? "TRANSPORTADORA" : "PATIO");

export interface User {
  id?: number;
  nome: string;
  email: string;
  grupo?: string;
  id_grupo?: number | null;
  subsistema?: string | null;
  permissoes?: any[];
  id_transportadora?: number | null;
  id_terminal?: number | null;
  id_motorista?: number | null;
}

// Defina o tipo para o contexto
interface AuthenticateContextType {
  user: User | null;
  login: (token: string, userData: User, rememberMe?: boolean) => void;
  logout: (arg?: any) => void;
  verifySession: () => Promise<boolean>;
  isLoadingPermissions: boolean;
}

// Crie o contexto com o tipo definido
const AuthenticateContext = createContext<AuthenticateContextType | undefined>(
  undefined
);

export const useAuth = () => {
  const context = useContext(AuthenticateContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthenticateProvider");
  }
  return context;
};

// Defina o tipo para os props do provider
interface AuthenticateProviderProps {
  children: ReactNode;
}

export const AuthenticateProvider: React.FC<AuthenticateProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem("user") || localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [isLoadingPermissions, setIsLoadingPermissions] = useState<boolean>(true);

  const clearStoredAuth = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    clearPersistedGridFilters();
    delete api.defaults.headers.common.Authorization;
  }, []);

  const onAuthenticate = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let token = urlParams.get("token");

    if (!token) {
      token = localStorage.getItem('token') || localStorage.getItem('token');
    }

    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      // Mantém onde estava
      if (localStorage.getItem('token')) {
          localStorage.setItem('token', token);
      } else {
          localStorage.setItem('token', token);
      }
    }
  }, []);

  const fetchPermissions = useCallback(async (userId: number, subsistema: string, idGrupo?: number | null) => {
    try {
      setIsLoadingPermissions(true);
      // O id_grupo é o grupo escolhido no login: sem ele o backend pega o
      // primeiro grupo do subsistema, que pode não ser o selecionado.
      const response = await api.post("/listar/gruposPermissoes", {
        id_usuario: userId,
        subsistema,
        id_grupo: idGrupo ?? null,
      });

      const fetchedPermissions = response.data.map((item: any) => ({
        action: item.permissoes?.acao,
        module: item.permissoes?.modulo?.toLowerCase(),
        submodule: item.permissoes?.submodulo?.toLowerCase(),
        subsistema: item.permissoes?.subsistema?.toUpperCase() ?? null,
      }));

      const storage = localStorage.getItem("token") ? localStorage : localStorage;
      storage.setItem("permissions", JSON.stringify(fetchedPermissions));
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setIsLoadingPermissions(false);
    }
  }, []);

  const login = useCallback((token: string, userData: User, rememberMe: boolean = false) => {
    const storage = rememberMe ? localStorage : localStorage;
    
    // Limpa o outro storage para evitar duplicidade/confusão
    const otherStorage = rememberMe ? localStorage : localStorage;
    otherStorage.removeItem("token");
    otherStorage.removeItem("user");
    otherStorage.removeItem("permissions");

    storage.setItem("token", token);
    storage.setItem("user", JSON.stringify(userData));
    
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    setUser(userData);
    
    if (userData.id) {
      fetchPermissions(userData.id, getSubsistema(userData), userData.id_grupo);
    } else {
      setIsLoadingPermissions(false);
    }
  }, [fetchPermissions]);

  const logout = useCallback(async (arg?: any) => {
    const isExpired = arg === true;

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("token");
      if (token) {
        await api.post('/autenticar/logout', { token });
      }
    } catch (error) {
      console.error("Erro ao realizar logout na API:", error);
    }

    clearStoredAuth();
    setUser(null);
    setIsLoadingPermissions(false);

    // Não chamamos setUser(null) aqui para evitar renderização intermediária antes do reload
    if (isExpired) {
      window.location.href = "/?sessionExpired=true";
    } else {
      window.location.href = "/";
    }
  }, [clearStoredAuth]);

  const verifySession = useCallback(async () => {
    const token = localStorage.getItem("token") || localStorage.getItem("token");
    if (!token) return false;

    try {
      const response = await api.post('/autenticar/verificaLogado', { token });
      if (!response.data.logado) {
        logout(true);
        return false;
      }
      return true;
    } catch (error: any) {
      console.error("Erro ao verificar sessão:", error);

      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout(true);
        return false;
      }

      return true;
    }
  }, [logout]);



  useEffect(() => {
    onAuthenticate();
    if (user?.id) {
      fetchPermissions(user.id, getSubsistema(user), user.id_grupo);
    } else {
      setIsLoadingPermissions(false);
    }
  }, [onAuthenticate, user, fetchPermissions]);

  return (
    <AuthenticateContext.Provider value={{ user, login, logout, verifySession, isLoadingPermissions }}>
      {children}
    </AuthenticateContext.Provider>
  );
};

// Hook personalizado para usar o contexto
export const useBreadcrumb = (): AuthenticateContextType => {
  const context = useContext(AuthenticateContext);
  if (!context) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
  }
  return context;
};
